import React, {
    useContext,
    useEffect,
    useState,
    useRef,
    createRef
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { kebabCase } from 'lodash';
import { CarouselContext, Slider, Slide } from 'pure-react-carousel';

import { h3 } from '~/styles/typography';
import { transition } from '~/styles/transition';
import { setMediumSpacing } from '~/utilities/spacing';
import useWindowSize from '~/hooks/useWindowSize';
import useOnWindowResize from '~/hooks/useOnWindowResize';
import { isBrowser, dynamicFontSize, formatText } from '~/utilities/helpers';

const StyledSlider = styled(Slider)`
    .fade-animation {
        ${transition(
            { speed: 1250, easingFunction: 'ease-in-out' },
            'transform',
            'opacity'
        )};
    }

    .hide {
        ${transition(
            { speed: 1250, easingFunction: 'ease-in-out' },
            'transform',
            'opacity'
        )};
        opacity: 0;
    }

    .show {
        ${transition(
            { speed: 1250, easingFunction: 'ease-in-out' },
            'transform',
            'opacity'
        )};
        opacity: 1;
    }
`;

const StyledSlide = styled(Slide)`
    .carousel__inner-slide {
        display: flex;
        align-items: center;
    }
`;

const Figure = styled.figure`
    display: flex;
    flex-direction: column;
    align-items: center;
    ${setMediumSpacing('gap')}
    padding-bottom: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        align-items: flex-start;
        padding-left: 30px;
        padding-bottom: 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        padding-left: 0;
    }
`;

const Quote = styled.blockquote`
    ${h3};
    color: ${({ theme }) => theme.colors.text.blue};
    font-size: ${dynamicFontSize(2.1, 4, 4.6)};
    line-height: 1.325;
    text-align: center;
    font-weight: ${({ theme }) => theme.fontWeights.bold};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        text-align: left;
        font-weight: ${({ theme }) => theme.fontWeights.black};
    }
`;

const Citation = styled.figcaption`
    font-family: ${({ theme }) => theme.fonts.heading};
    color: ${({ theme }) => theme.colors.accent.blue};
    text-transform: uppercase;
    font-weight: ${({ theme }) => theme.fontWeights.black};
    font-size: ${dynamicFontSize(1.5, 2, 2.4)};
`;

const QuoteCarousel = ({ quotes, wrapperRef }) => {
    const windowSizes = useWindowSize();
    const carouselContext = useContext(CarouselContext);
    const [wrapperWidth, setWrapperWidth] = useState(0);
    const [tallestQuote, setTallestQuote] = useState(0);
    const quoteRefs = useRef(quotes.map(() => createRef()));

    useOnWindowResize(() => {
        if (wrapperRef?.current && isBrowser) {
            let newWidth = wrapperRef.current.clientWidth;

            if (newWidth !== wrapperWidth) {
                setWrapperWidth(newWidth);
            }
        }

        carouselContext.setStoreState({
            isIntrinsicHeight: !windowSizes.includes('tablet'),
            orientation: 'horizontal'
        });
    }, true);

    useEffect(() => {
        setTimeout(() => {
            const tallestSlideHeight = Math.max(
                ...quoteRefs.current.map((ref) => {
                    return ref.current?.scrollHeight || 0;
                })
            );
            setTallestQuote(tallestSlideHeight);
        }, 500);
    }, [quoteRefs, wrapperWidth]);

    useEffect(() => {
        carouselContext.setStoreState({
            naturalSlideWidth: wrapperWidth,
            naturalSlideHeight: tallestQuote
        });
    }, [wrapperWidth, tallestQuote, carouselContext]);

    return (
        <StyledSlider classNameAnimation="fade-animation">
            {quotes.map(({ quote, citation }, i) => (
                <StyledSlide
                    classNameHidden="hide"
                    classNameVisible="show"
                    index={i}
                    key={kebabCase(quote)}
                >
                    <Figure ref={quoteRefs.current[i]}>
                        <Quote>{formatText(quote)}</Quote>
                        {citation ? (
                            <Citation>{formatText(citation)}</Citation>
                        ) : null}
                    </Figure>
                </StyledSlide>
            ))}
        </StyledSlider>
    );
};

QuoteCarousel.propTypes = {
    quotes: PropTypes.arrayOf(
        PropTypes.shape({
            quote: PropTypes.string.isRequired,
            citation: PropTypes.string
        })
    ).isRequired,
    wrapperRef: PropTypes.shape({})
};

export default QuoteCarousel;
